
import * as React from 'react';
import { Omit } from '../Omit';
import { Fragment } from './Fragment';

/**
 * @public
 */
export type IChildrenAsRenderProp<TStateProps> = React.Props<{}>['children'] | ((props: TStateProps) => JSX.Element | null);

/**
 * @public
 */
export interface IRenderProps<TStateProps> {
    children?: IChildrenAsRenderProp<TStateProps>;
}

/**
 * @public
 */
export function renderChildrenWithProps<TStateProps>(children: IChildrenAsRenderProp<TStateProps>, props: TStateProps): JSX.Element | null {
    return isRenderProp(children) ?
        children(props) :
        (
            <Fragment>{ children }</Fragment>
        );
}

/**
 * @internal
 */
export interface IStatePropsUpdaterProps<T> {
    stateProps: T;
    onUpdate: (actionSetProsp: T) => void;
}

/**
 * Utility component used to support converting between a render-prop pattern and a callback-based state update.
 * Normally, callers should use the render-prop pattern. However, some component may need to use a
 * state-based callback pattern for compatibility reasons.
 *
 * @internal
 */
export class StatePropsUpdater<T> extends React.PureComponent<IStatePropsUpdaterProps<T>> {
    public render(): JSX.Element | null {
        return null;
    }

    public componentDidMount(): void {
        this.props.onUpdate(this.props.stateProps);
    }

    public componentDidUpdate(previousProps: IStatePropsUpdaterProps<T>): void {
        const {
            stateProps,
            onUpdate
        } = this.props;

        if (stateProps !== previousProps.stateProps ||
            onUpdate !== previousProps.onUpdate) {
            onUpdate(stateProps);
        }
    }
}

/**
 * The withBoth decorator joins together two `With...` components to reduce the amount of nesting
 * required when using them.
 * The result is a component which takes all the props across both types, and which takes a callback that
 * accepts the outputs from both types.
 * @param componentTypeA - The outer component type.
 * @param componentTypeB - The inner component type.
 * @internal
 * @deprecated This is too expensive.
 */
export function withBothRenderProps<
    ARenderProps extends IRenderProps<AStateProps>,
    BRenderProps extends IRenderProps<BStateProps>,
    AStateProps,
    BStateProps
>(
    componentTypeA: React.ComponentType<ARenderProps & IRenderProps<AStateProps>>,
    componentTypeB: React.ComponentType<BRenderProps & IRenderProps<BStateProps>>
): React.ComponentType<Omit<ARenderProps, 'children'> & Omit<BRenderProps, 'children'> & IRenderProps<AStateProps & BStateProps>> {
    const WithA = componentTypeA;
    const WithB = componentTypeB;

    class WithBothRenderProps extends React.PureComponent<
        Omit<ARenderProps, 'children'> & Omit<BRenderProps, 'children'> & IRenderProps<AStateProps & BStateProps>
        > {
        public render(): JSX.Element {
            const {
                children,
                ...props
            } = this.props as IRenderProps<AStateProps & BStateProps>;

            return (
                <WithA { ...props as ARenderProps } >
                    {
                        (aStateProps: AStateProps) => {
                            return (
                                <WithB { ...props as BRenderProps }>
                                    {
                                        (bStateProps: BStateProps) => {
                                            return renderChildrenWithProps(children, {
                                                ...(aStateProps as {}),
                                                ...(bStateProps as {})
                                            } as (AStateProps & BStateProps));
                                        }
                                    }
                                </WithB>
                            );
                        }
                    }
                </WithA>
            );
        }
    }

    return WithBothRenderProps;
}

/**
 * @internal
 */
export function isRenderProp<T>(
    children: React.Props<{}>['children'] | ((props: T) => JSX.Element | null)
): children is ((props: T) => JSX.Element | null) {
    return typeof children === 'function';
}
